// The default catalog (AWARDS.md "Weekly awards", "Season awards",
// "Milestones"). Every row is data: a picker over a metric and the lines
// that get written when it lands. Adding an honor is adding a row here.
// Lines use placeholders the engine fills from the winner's detail:
// {team} {owner} {value} {opponent} {opponentOwner} {margin} {player}
// {share} {projected} {actual} {period} {season} {trophy} {finalLine}
// {firstHalf} {secondHalf}.

import type { MilestoneDefinition, AwardDefinition, Catalog } from './types';

export const WEEKLY_AWARDS: AwardDefinition[] = [
  {
    id: 'high-score',
    scope: 'period',
    name: 'High Score',
    tone: 'glory',
    icon: 'crown',
    blurb: 'Most points in the league this week.',
    pick: { over: 'team-period', metric: 'points', best: 'max' },
    unit: 'points',
    lines: [
      '{team} put up {value}, the most in the league this week.',
      '{value} points. Nobody else came close; {owner} owns the week.',
      '{owner} dropped {value} on the league. Take a bow.',
    ],
  },
  {
    id: 'low-score',
    scope: 'period',
    name: 'Toilet Bowl',
    tone: 'shame',
    icon: 'toilet',
    blurb: 'Fewest points this week. Somebody has to.',
    pick: { over: 'team-period', metric: 'points', best: 'min' },
    unit: 'points',
    lines: [
      '{team} managed {value}. The bench sends its condolences.',
      '{value} points. {owner} would like to speak to the manager.',
      'Low score of the week: {team}, {value}. It happens to everyone. This week it happened to {owner}.',
    ],
  },
  {
    id: 'closest-game',
    scope: 'period',
    name: 'Nail-biter',
    tone: 'fun',
    icon: 'target',
    blurb: 'The closest matchup of the week.',
    pick: { over: 'matchup', metric: 'margin', best: 'min' },
    unit: 'margin',
    lines: [
      '{team} edged {opponent} by {margin}. Somebody is refreshing the stat corrections.',
      'Decided by {margin}: {team} over {opponent}. Every point mattered, for once.',
      "{owner} beat {opponentOwner} by {margin}. That's one catch. One.",
    ],
  },
  {
    id: 'blowout',
    scope: 'period',
    name: 'Blowout',
    tone: 'fun',
    icon: 'bolt',
    blurb: 'The biggest margin of the week.',
    pick: { over: 'matchup', metric: 'margin', best: 'max' },
    unit: 'margin',
    lines: [
      "{team} beat {opponent} by {margin}. That's not a game, that's a statement.",
      '{owner} won by {margin}. {opponentOwner} has been asked to stop checking the score.',
      "{margin} points. {team} over {opponent}, and it wasn't that close.",
    ],
  },
  {
    id: 'best-bench',
    scope: 'period',
    name: 'Left It on the Bench',
    tone: 'shame',
    icon: 'armchair',
    blurb: 'Most bench points this week.',
    pick: { over: 'team-period', metric: 'benchPoints', best: 'max' },
    unit: 'points',
    lines: [
      '{team} left {value} points on the bench. Those were starters, {owner}. All of them.',
      '{value} bench points for {team}. The right guys were on the roster, just not in the lineup.',
      "{owner}'s bench scored {value}. The bench is not supposed to be the best part.",
    ],
  },
  {
    id: 'waiver-steal',
    scope: 'period',
    name: 'Waiver Steal',
    tone: 'glory',
    icon: 'magnet',
    blurb: 'The pickup that paid off most this week.',
    pick: { over: 'transaction', best: 'max' },
    unit: 'points',
    lines: [
      '{owner} grabbed {player} off the wire and got {value} out of him. Robbery.',
      '{player} scored {value} for {team} the week {owner} picked him up.',
      'Best pickup of the week: {player}, {value} points, {team}. The wire giveth.',
    ],
  },
  {
    id: 'heartbreaker',
    scope: 'period',
    name: 'Heartbreaker',
    tone: 'shame',
    icon: 'heart-broken',
    blurb: 'Most points in a loss.',
    pick: { over: 'team-period', metric: 'pointsInLoss', best: 'max' },
    unit: 'points',
    lines: [
      '{team} scored {value} and lost. To {opponent}. By {margin}.',
      '{value} points, no win. {owner} did everything right except the schedule.',
      "{owner} put up {value} and it wasn't enough. {opponentOwner} sends thoughts and prayers.",
    ],
  },
  {
    id: 'bank-robbery',
    scope: 'period',
    name: 'Bank Robbery',
    tone: 'fun',
    icon: 'dice',
    blurb: 'Fewest points in a win.',
    pick: { over: 'team-period', metric: 'pointsInWin', best: 'min' },
    unit: 'points',
    lines: [
      '{team} won with {value}. {opponent} scored less, somehow.',
      "{owner} stole one: {value} was enough this week. Don't ask how.",
      '{value} points and a W. {team} would like to thank {opponent} for their cooperation.',
    ],
  },
  {
    id: 'one-man-show',
    scope: 'period',
    name: 'One-Man Show',
    tone: 'fun',
    icon: 'star',
    blurb: 'The biggest single starter score in the league.',
    pick: { over: 'team-period', metric: 'topStarterPoints', best: 'max' },
    unit: 'points',
    lines: [
      '{player} scored {value} for {team}, {share}% of the whole team.',
      '{player}: {value}. The rest of {team}: also present.',
      '{owner} started {player} and {player} did the rest. {value} points, {share}% of the total.',
    ],
  },
  {
    id: 'overachiever',
    scope: 'period',
    name: 'Overachiever',
    tone: 'glory',
    icon: 'rocket',
    blurb: 'Beat the projection by the most.',
    pick: { over: 'team-period', metric: 'overProjection', best: 'max', min: 0.1 },
    unit: 'points',
    lines: [
      '{team} beat the projection by {value}. Nobody saw it coming, including {owner}.',
      'Projected {projected}, scored {actual}. {owner} is not answering questions about how.',
      '{owner} outran the projection by {value}. The spreadsheet is furious.',
    ],
  },
];

export const SEASON_AWARDS: AwardDefinition[] = [
  {
    id: 'champion',
    scope: 'season',
    name: 'Champion',
    tone: 'glory',
    icon: 'trophy',
    blurb: 'Won the playoffs. The one that matters.',
    pick: { over: 'bracket', slot: 'champion' },
    unit: 'none',
    lines: [
      '{team} are your {season} champions. {finalLine}',
      'The {trophy} goes to {team}. {finalLine}',
    ],
  },
  {
    id: 'runner-up',
    scope: 'season',
    name: 'Runner-up',
    tone: 'fun',
    icon: 'medal',
    blurb: 'Made the final and lost it.',
    pick: { over: 'bracket', slot: 'runner-up' },
    unit: 'none',
    lines: [
      '{team} made the final and lost it. Next year.',
      'Second place: {team}. Close enough to see it.',
    ],
  },
  {
    id: 'points-title',
    scope: 'season',
    name: 'Points Title',
    tone: 'glory',
    icon: 'chart',
    blurb: 'Most points in the regular season.',
    pick: { over: 'team-season', metric: 'pointsFor', best: 'max' },
    unit: 'points',
    lines: [
      '{team} scored {value} in the regular season, more than anyone.',
      "The most points all year: {team}, {value}. The standings didn't always agree.",
    ],
  },
  {
    id: 'last-place',
    scope: 'season',
    name: 'Last Place',
    tone: 'shame',
    icon: 'toilet',
    blurb: 'The bottom of the league. Somebody has to.',
    pick: { over: 'bracket', slot: 'last-place' },
    unit: 'none',
    lines: [
      '{team} finished last. The punishment is between {owner} and the league.',
      'Last place: {team}. Somebody has to, and this year it was {owner}.',
    ],
  },
  {
    id: 'week-of-the-year',
    scope: 'season',
    name: 'Week of the Year',
    tone: 'glory',
    icon: 'flame',
    blurb: 'The highest single-week score of the season.',
    pick: { over: 'team-season', metric: 'bestWeek', best: 'max' },
    unit: 'points',
    lines: [
      "{team}'s {value} in Week {period} was the best week anyone had.",
      '{value} in Week {period}. {owner} peaked, and it was glorious.',
    ],
  },
  {
    id: 'juggernaut',
    scope: 'season',
    name: 'Juggernaut',
    tone: 'glory',
    icon: 'sword',
    blurb: 'The longest win streak.',
    pick: { over: 'team-season', metric: 'winStreak', best: 'max', min: 2 },
    unit: 'count',
    lines: [
      '{team} won {value} straight.',
      '{value} wins in a row for {owner}. The league noticed.',
    ],
  },
  {
    id: 'comeback-kid',
    scope: 'season',
    name: 'Comeback Kid',
    tone: 'glory',
    icon: 'mountain',
    blurb: 'The biggest turnaround from a losing first half.',
    pick: { over: 'team-season', metric: 'comeback', best: 'max', min: 1 },
    // The line carries the story ("from 2–5 to 6–1"); a bare number would not.
    unit: 'none',
    lines: [
      '{owner} started {firstHalf} and finished {secondHalf}. Never count them out.',
      'From {firstHalf} to {secondHalf}: {team} turned the season around.',
    ],
  },
  {
    id: 'punching-bag',
    scope: 'season',
    name: 'Punching Bag',
    tone: 'shame',
    icon: 'ghost',
    blurb: 'Most points scored against.',
    pick: { over: 'team-season', metric: 'pointsAgainst', best: 'max' },
    unit: 'points',
    lines: [
      '{team} had {value} scored against them. The schedule had it out for {owner}.',
      "{value} points against. {owner} played everyone's best week.",
    ],
  },
  {
    id: 'benchwarmer',
    scope: 'season',
    name: 'Benchwarmer',
    tone: 'shame',
    icon: 'armchair',
    blurb: 'Most bench points over the season.',
    pick: { over: 'team-season', metric: 'benchPoints', best: 'max' },
    unit: 'points',
    lines: [
      "{team} left {value} points on the bench this season. That's a whole extra team.",
      "{value} bench points across the season. {owner}'s reserves would like a word.",
    ],
  },
  {
    id: 'waiver-wizard',
    scope: 'season',
    name: 'Waiver Wizard',
    tone: 'glory',
    icon: 'sparkles',
    blurb: 'Most points from players picked up during the season.',
    pick: { over: 'team-season', metric: 'pickupPoints', best: 'max', min: 0.1 },
    unit: 'points',
    lines: [
      '{owner} squeezed {value} points out of the waiver wire.',
      '{value} points from pickups. {team} was built on Wednesdays.',
    ],
  },
  {
    id: 'trade-shark',
    scope: 'season',
    name: 'Trade Shark',
    tone: 'fun',
    icon: 'briefcase',
    blurb: 'Most trades completed.',
    pick: { over: 'team-season', metric: 'trades', best: 'max', min: 1 },
    unit: 'count',
    lines: [
      '{owner} made {value} trades. The phone never stopped.',
      '{value} trades completed. {team} never sat still.',
    ],
  },
  {
    // PICK'EM (scenario I6, PLAN-2026-10-18-families-and-newcomers §9.1): the
    // team that called the league's games best, a point a winner and half a
    // tie, so a team out of the race still had one to win.
    id: 'best-picker',
    scope: 'season',
    name: 'Best Picker',
    tone: 'glory',
    icon: 'target',
    blurb: 'Most pick’em points: called the league’s games best.',
    pick: { over: 'team-season', metric: 'pickemPoints', best: 'max', min: 1 },
    unit: 'points',
    lines: [
      '{owner} called the league’s games best: {value} pick’em points.',
      '{value} pick’em points for {team}. Nobody read the league better.',
    ],
  },
];

export const MILESTONES: MilestoneDefinition[] = [
  {
    id: 'first-blood',
    name: 'First Blood',
    tone: 'glory',
    icon: 'flag',
    blurb: 'Win a game in this league.',
    metric: 'wins',
    scope: 'season',
    unit: 'count',
    tiers: [{ tier: 'bronze', threshold: 1 }],
    line: "{owner}'s first win of the season.",
  },
  {
    id: 'century-club',
    name: 'Century Club',
    tone: 'glory',
    icon: 'flame',
    blurb: 'Score big in a single week.',
    metric: 'bestWeek',
    scope: 'season',
    unit: 'points',
    tiers: [
      { tier: 'bronze', threshold: 100 },
      { tier: 'silver', threshold: 150 },
      { tier: 'gold', threshold: 200 },
    ],
    line: 'Scored {value} in a week.',
  },
  {
    id: 'hot-streak',
    name: 'Hot Streak',
    tone: 'glory',
    icon: 'sword',
    blurb: 'Win several in a row.',
    metric: 'winStreak',
    scope: 'season',
    unit: 'count',
    tiers: [
      { tier: 'bronze', threshold: 3 },
      { tier: 'silver', threshold: 5 },
      { tier: 'gold', threshold: 8 },
    ],
    line: 'Won {value} in a row.',
  },
  {
    id: 'steamroller',
    name: 'Steamroller',
    tone: 'fun',
    icon: 'bolt',
    blurb: 'Win by a lot.',
    metric: 'biggestMargin',
    scope: 'season',
    unit: 'margin',
    tiers: [
      { tier: 'bronze', threshold: 30 },
      { tier: 'silver', threshold: 50 },
      { tier: 'gold', threshold: 75 },
    ],
    line: 'Won by {value}.',
  },
  {
    id: 'iron-man',
    name: 'Iron Man',
    tone: 'glory',
    icon: 'shield',
    blurb: 'Weeks in a row with a full, active lineup.',
    metric: 'ironStreak',
    scope: 'season',
    unit: 'count',
    tiers: [
      { tier: 'bronze', threshold: 5 },
      { tier: 'silver', threshold: 10 },
      { tier: 'gold', threshold: 14 },
    ],
    line: '{weeks} in a row with a full, active lineup.',
  },
  {
    id: 'perfect-sunday',
    name: 'Perfect Sunday',
    tone: 'glory',
    icon: 'star',
    blurb: 'Start the best lineup your roster had.',
    metric: 'perfectWeeks',
    scope: 'season',
    unit: 'count',
    tiers: [
      { tier: 'bronze', threshold: 1 },
      { tier: 'silver', threshold: 3 },
      { tier: 'gold', threshold: 6 },
    ],
    line: 'Started the best lineup available: {weeks}.',
  },
  {
    id: 'wire-worker',
    name: 'Wire Worker',
    tone: 'fun',
    icon: 'magnet',
    blurb: 'Win waiver claims.',
    metric: 'claimsWon',
    scope: 'season',
    unit: 'count',
    tiers: [
      { tier: 'bronze', threshold: 3 },
      { tier: 'silver', threshold: 10 },
      { tier: 'gold', threshold: 20 },
    ],
    line: 'Won {claims}.',
  },
  {
    id: 'dealmaker',
    name: 'Dealmaker',
    tone: 'fun',
    icon: 'briefcase',
    blurb: 'Complete trades.',
    metric: 'trades',
    scope: 'season',
    unit: 'count',
    tiers: [
      { tier: 'bronze', threshold: 1 },
      { tier: 'silver', threshold: 3 },
      { tier: 'gold', threshold: 6 },
    ],
    line: 'Completed {trades}.',
  },
  {
    id: 'giant-killer',
    name: 'Giant Killer',
    tone: 'glory',
    icon: 'hammer',
    blurb: 'Beat a team with a better record.',
    metric: 'giantKills',
    scope: 'season',
    unit: 'count',
    tiers: [
      { tier: 'bronze', threshold: 1 },
      { tier: 'silver', threshold: 3 },
      { tier: 'gold', threshold: 6 },
    ],
    line: 'Beat a better team {times}.',
  },
  {
    id: 'escape-artist',
    name: 'Escape Artist',
    tone: 'fun',
    icon: 'dice',
    blurb: 'Win by less than three.',
    metric: 'escapes',
    scope: 'season',
    unit: 'count',
    tiers: [
      { tier: 'bronze', threshold: 1 },
      { tier: 'silver', threshold: 3 },
      { tier: 'gold', threshold: 5 },
    ],
    line: 'Won {games} by under three points.',
  },
  {
    id: 'frequent-flyer',
    name: 'Frequent Flyer',
    tone: 'shame',
    icon: 'toilet',
    blurb: 'Collect Toilet Bowls.',
    metric: 'lowScores',
    scope: 'season',
    unit: 'count',
    tiers: [
      { tier: 'bronze', threshold: 2 },
      { tier: 'silver', threshold: 4 },
      { tier: 'gold', threshold: 7 },
    ],
    line: '{bowls}. Somebody frame this.',
  },
  {
    id: 'decorated',
    name: 'Decorated',
    tone: 'glory',
    icon: 'medal',
    blurb: 'Weekly awards won, career.',
    metric: 'weeklyAwards',
    scope: 'career',
    unit: 'count',
    tiers: [
      { tier: 'bronze', threshold: 5 },
      { tier: 'silver', threshold: 15 },
      { tier: 'gold', threshold: 30 },
      { tier: 'platinum', threshold: 60 },
    ],
    line: '{awards}, career.',
  },
  {
    id: 'playoff-bound',
    name: 'Playoff Bound',
    tone: 'glory',
    icon: 'podium',
    blurb: 'Playoff appearances, career.',
    metric: 'playoffTrips',
    scope: 'career',
    unit: 'count',
    tiers: [
      { tier: 'bronze', threshold: 1 },
      { tier: 'silver', threshold: 3 },
      { tier: 'gold', threshold: 5 },
    ],
    line: '{trips}.',
  },
  {
    id: 'dynasty',
    name: 'Dynasty',
    tone: 'glory',
    icon: 'crown',
    blurb: 'Championships, career.',
    metric: 'titles',
    scope: 'career',
    unit: 'count',
    tiers: [
      { tier: 'bronze', threshold: 1 },
      { tier: 'silver', threshold: 2 },
      { tier: 'gold', threshold: 3 },
      { tier: 'platinum', threshold: 5 },
    ],
    line: '{titles}.',
  },
];

export const DEFAULT_CATALOG: Catalog = {
  awards: [...WEEKLY_AWARDS, ...SEASON_AWARDS],
  milestones: MILESTONES,
};
